import { Box, Link, Stack } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import { SxProps } from '@mui/joy/styles/types';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { IBomPart } from 'bom/types';
import { TextHighlighter, TextLine } from 'core/elements';
import { ExternalHazardousIcon, ExternalMarketingMemo, ExternalPartStatus } from 'parts/elements';
import { PartNumberHelper } from 'parts/helpers';
import { Color, Direction } from 'theme/enums';

interface IProps {
  bomPart?: IBomPart;
  onPartClick?: (bomPart: IBomPart) => void;
  getDisplayedPartNumber?: (bomPart: IBomPart) => string;
  wrapperSx?: SxProps;
  isDeleted?: boolean;
}

export const BomPartNumber = ({
  bomPart,
  onPartClick,
  getDisplayedPartNumber = defaultGetDisplayedPartNumber,
  wrapperSx = {},
  isDeleted = false,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { user } = useAuthUser();

  const displayedPartNumber = getDisplayedPartNumber(bomPart);

  const isAlternateVersionEnabled =
    Boolean(bomPart?.alternateVersionEnabled) || Boolean(user?.alternateVersionEnabled);

  const showVendorPartNumber = bomPart?.vendorPartNumber !== displayedPartNumber;
  const showAlternateVendorPartNumber =
    isAlternateVersionEnabled && !!bomPart?.alternateVendorPartNumber;

  return (
    <Stack direction={Direction.ROW} gap={0.5}>
      {isAlternateVersionEnabled && (
        <ExternalHazardousIcon isHazardous={bomPart?.hazardous} style={{ marginTop: 4 }} />
      )}

      {/* minWidth:0 lets the truncated memo chip ellipsis instead of widening the column */}
      <Box sx={{ minWidth: 0 }}>
        <TextLine
          text={getPartNumberValue(theme, bomPart, onPartClick, displayedPartNumber, isDeleted)}
          color={isDeleted ? Color.DANGER : null}
          sx={wrapperSx}
          bold
        />

        {isAlternateVersionEnabled && (
          <Stack sx={{ flex: 1, gap: 0.5, padding: '4px 0' }}>
            <ExternalPartStatus value={bomPart?.externalPartStatus} />
            <ExternalMarketingMemo marketingMemo={bomPart?.marketingMemo} inline truncate />
          </Stack>
        )}

        {showVendorPartNumber && <TextLine text={bomPart?.vendorPartNumber} isSubText />}
        {showAlternateVendorPartNumber && (
          <TextLine
            label={t('labelAlternateVendorPartNumber')}
            text={bomPart?.alternateVendorPartNumber}
            isSubText
          />
        )}
      </Box>
    </Stack>
  );
};

const defaultGetDisplayedPartNumber = (bomPart: IBomPart) =>
  PartNumberHelper.getDisplayedPartNumber(bomPart?.partArr);

const getPartNumberValue = (
  theme: ReturnType<typeof useTheme>,
  bomPart: IBomPart,
  onPartClick: (bomPart: IBomPart) => void | undefined,
  displayedPartNumber: string,
  isDeleted = false
) => {
  const linkColor = getLinkColor(!!bomPart?.partArr?.verified, isDeleted, !!onPartClick);

  if (bomPart?.partArr?.wildcard) {
    return (
      <TextHighlighter
        text={displayedPartNumber}
        highlight={'?'}
        highlightColor={Color.DANGER}
        color={isDeleted ? Color.DANGER : null}
      />
    );
  }

  if (!onPartClick) {
    return <TextLine color={linkColor} text={displayedPartNumber} bold />;
  }

  return (
    <Link
      onClick={() => onPartClick(bomPart)}
      color={linkColor}
      sx={{
        textWrap: 'wrap',
        // Unverified parts (linkColor is null) use text.primary for better dark mode visibility
        ...(!linkColor ? { color: theme.palette.text.primary } : {}),
        '&:hover': {
          ...(!linkColor ? { color: theme.palette.primary.softColor } : {}),
        },
      }}
    >
      {displayedPartNumber}
    </Link>
  );
};

const getLinkColor = (isVerified: boolean, isDeleted = false, isClickable = false) => {
  if (isDeleted) {
    return Color.DANGER;
  }

  if (isClickable && isVerified) {
    return Color.PRIMARY;
  }

  return null;
};
